import { useEffect } from "react";
import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { LayoutDashboard, CalendarDays, Plus, SlidersHorizontal, Sparkles } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/shifts/new", label: "Add Shift", icon: Plus, id: "add-shift", primary: true },
  { to: "/settings", label: "Salary", icon: SlidersHorizontal, id: "settings" },
  { to: "/premium", label: "Premium", icon: Sparkles, id: "premium" },
];

export const Logo = () => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
    <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-500 font-heading text-lg font-black text-[#0B0F17]">S</span>
    <span className="font-heading text-2xl font-extrabold uppercase tracking-tight">Shift<span className="text-amber-500">Mate</span></span>
  </Link>
);

const Sidebar = () => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-[#0D121C] px-5 py-6 md:flex">
    <Logo />
    <nav className="mt-10 flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, id, primary }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={`sidebar-nav-${id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
              primary
                ? "mt-2 mb-2 bg-amber-500 text-[#0B0F17] hover:bg-amber-400"
                : isActive
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label === "Salary" ? "Salary Settings" : label}
        </NavLink>
      ))}
    </nav>
    <div className="mt-auto flex items-center gap-3 border-t border-border pt-5" data-testid="sidebar-user">
      <UserButton />
      <span className="text-xs text-muted-foreground">Your account</span>
    </div>
  </aside>
);

const BottomNav = () => (
  <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-[#0D121C]/95 backdrop-blur-xl md:hidden">
    <div className="mx-auto grid max-w-md grid-cols-5 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2">
      {NAV.map(({ to, label, icon: Icon, id, primary }) => (
        <NavLink key={to} to={to} data-testid={`bottom-nav-${id}`} className="flex flex-col items-center gap-1">
          {({ isActive }) =>
            primary ? (
              <span className="-mt-7 grid h-14 w-14 place-items-center rounded-2xl bg-amber-500 text-[#0B0F17] shadow-[0_8px_24px_rgba(245,158,11,0.35)] transition-transform active:scale-95">
                <Icon className="h-6 w-6" strokeWidth={2.5} />
              </span>
            ) : (
              <>
                <Icon className={`h-5 w-5 ${isActive ? "text-amber-500" : "text-muted-foreground"}`} />
                <span className={`text-[10px] font-semibold ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
              </>
            )
          }
        </NavLink>
      ))}
    </div>
  </nav>
);

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

export const AppLayout = () => (
  <div className="grain min-h-screen bg-background">
    <ScrollTop />
    <Sidebar />
    <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl md:hidden">
      <Logo />
      <UserButton />
    </header>
    <main className="relative z-10 md:pl-64">
      <div className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 md:pb-12 md:pt-10 lg:px-10">
        <Outlet />
      </div>
    </main>
    <BottomNav />
  </div>
);

export const Protected = () => (
  <>
    <Show when="signed-in"><AppLayout /></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);
